import { useState } from "react";
import { getFamilyConfigForHost } from "../lib/families";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { usePublicBranding } from "../hooks/usePublicBranding";
import { readCachedBranding } from "../lib/brandingCache";

type Variant = "on-dark" | "on-light";

interface LogoProps {
  variant?: Variant;
  size?: number;
  className?: string;
}

/**
 * Renders the family logo. Resolution order (best to worst):
 *   1. The current membership's uploaded logo (signed-in user, /me)
 *   2. The public-by-host branding endpoint (login & invite pages)
 *   3. localStorage cache from a previous visit (avoids first-paint flash)
 *   4. The static logo bundled in /public, keyed by hostname
 *   5. A styled text logotype using the family name
 *
 * Note on variants:
 *   - `on-dark`  = the surrounding background is dark, so we need a LIGHT logo
 *   - `on-light` = the surrounding background is light, so we need a DARK logo
 */
export function Logo({ variant = "on-light", size = 48, className }: LogoProps) {
  const [failed, setFailed] = useState(false);
  const meQuery = useMe();
  const membership = resolveMembership(meQuery.data);
  const publicBranding = usePublicBranding();
  const familyConfig = getFamilyConfigForHost();
  // Synchronous read on first render — paints the right logo immediately
  // when the user has visited before, instead of flashing the bundled one.
  const cached = useState(() => readCachedBranding())[0];

  const familyName =
    membership?.familyName
    ?? publicBranding?.name
    ?? familyConfig?.name
    ?? "Family";
  const altText = familyConfig?.logoAlt
    ? `The ${familyConfig.logoAlt} family logo`
    : `The ${familyName} family logo`;

  // Convention (matches the existing static config): the `light` variant is
  // designed for LIGHT backgrounds, `dark` for DARK backgrounds.
  const uploadedUrl =
    variant === "on-dark"
      ? membership?.logoDarkUrl
        ?? publicBranding?.logoDarkUrl
        ?? cached?.logoDarkUrl
        ?? null
      : membership?.logoLightUrl
        ?? publicBranding?.logoLightUrl
        ?? cached?.logoLightUrl
        ?? null;

  const fallbackFile =
    variant === "on-dark" ? familyConfig?.logo.dark : familyConfig?.logo.light;

  // No generic image fallback on purpose: a family with no logo of its own
  // must not inherit another family's crest. `src` stays null and the text
  // logotype below renders their name instead.
  const src = uploadedUrl ?? fallbackFile ?? null;

  if (failed || !src) {
    return (
      <span
        className={className}
        style={{
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontStyle: "italic",
          fontWeight: 700,
          fontSize: size * 0.5,
          letterSpacing: "0.02em",
          color: variant === "on-dark" ? "#e5e7eb" : "#111827",
        }}
      >
        {familyName}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={altText}
      onError={() => setFailed(true)}
      className={className}
      style={{
        height: size,
        width: "auto",
        objectFit: "contain",
        display: "block",
      }}
    />
  );
}
